import { getUserGetMyCompanies, getUserGetMyMspCompanies, getCspMktGetAwsSubscription, getCspMktGetAwsMarketplaceOffers } from '@apis/Customers';
import { AwsMarketplaceOffer, AwsMarketplaceSubscription, Company } from '@apis/Customers/model';
import styled from '@emotion/styled';
import { Accordion, Box, Card, Divider, Grid, Group, LoadingOverlay, Text, Title, UnstyledButton, useMantineTheme } from '@mantine/core';
import { useDi } from '@root/Services/DI';
import { FormatService } from '@root/Services/FormatService';
import { Logger } from '@root/Services/Logger';

import { Router } from '@root/Services/Router/Router';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { ChevronRight } from 'tabler-icons-react';
import { useNav } from '@root/Services/NavigationService';
import { BasicRouteLoader } from '@root/Services/Router/BasicRouteLoader';
import { AwsOnboardingService } from '@root/Services/Customers/AwsOnboarding';
import { MspService } from '@root/Services/MspService';
import { PrivateOfferFulfillment } from './CspMarketplace/AwsPrivateOffers';
import { CollectSalesInfo } from './CspMarketplace/CollectSalesInfo';

export function AwsMarketplaceLandingPage() {
    const theme = useMantineTheme();
    const routeLoader = useDi(BasicRouteLoader);
    const nav = useNav();
    const router = useDi(Router);
    const logger = useDi(Logger);
    const awsOnboardingSvc = useDi(AwsOnboardingService);
    const subscriptionId = awsOnboardingSvc.awsToken;
    const [subscription, setSubscription] = useState<AwsMarketplaceSubscription>();
    const [offer, setOffer] = useState<AwsMarketplaceOffer>();
    const [companies, setCompanies] = useState<Company[]>();
    const [mspCompany, setMspCompany] = useState<Company | null>(null);
    const fail = () => router.navigate('/_register');

    useEffect(() => {
        const params = nav.getData('subscriptionId', 'contactSales');

        if (params.subscriptionId != undefined && params.subscriptionId != subscriptionId) {
            awsOnboardingSvc.setCookie(params.subscriptionId);
        } else if (!subscriptionId && !params.subscriptionId) {
            fail();
        } else if (subscriptionId != undefined) {
            (async () => {
                try {
                    const companies = await getUserGetMyCompanies();
                    const mspCompanies = await getUserGetMyMspCompanies();
                    const offers = await getCspMktGetAwsMarketplaceOffers();
                    companies.push(...mspCompanies);
                    setMspCompany(companies.find((c) => c.Type === 'Msp') ?? null);
                    setCompanies(companies.filter((c) => c.Type !== 'Msp').sort((a, b) => (a.CompanyName! > b.CompanyName! ? 1 : -1)));
                    const subscription = await getCspMktGetAwsSubscription({ subscriptionId: subscriptionId });
                    if (!subscription) {
                        fail();
                    } else {
                        const offer = offers.find((o) => o.ProductCode === subscription.ProductCode);
                        setOffer(offer);
                        setSubscription(subscription);
                    }
                } catch (err) {
                    logger.error('Error while getting current user companies', err);
                    fail();
                }
            })();
        }
    }, [subscriptionId]);

    return !companies || !subscription ? (
        <LoadingOverlay visible={true} />
    ) : subscription.IsFreeTrial ? (
        <CollectSalesInfo subscription={subscription} />
    ) : !offer || !offer.IsPrivateOnly ? (
        <SubscriptionConfirmation companies={companies} mspCompany={mspCompany} subscription={subscription} />
    ) : subscription.PrivateOfferUrl ? (
        <PrivateOfferFulfillment companies={companies} subscription={subscription} offer={offer} />
    ) : (
        <CollectSalesInfo subscription={subscription} />
    );
}

function SubscriptionConfirmation({
    companies,
    mspCompany,
    subscription,
}: {
    companies: Company[];
    mspCompany: Company | null;
    subscription: AwsMarketplaceSubscription;
}) {
    const theme = useMantineTheme();
    const hasCompanies = companies.length > 0;
    const [openSection, setOpenSection] = useState<string>(hasCompanies ? 'select' : 'register');
    const setSection = useCallback(
        (section: string | null) => {
            if (section !== null) {
                setOpenSection(section);
            }
        },
        [openSection]
    );
    useEffect(() => {
        const originalBg = document.body.style.background;
        document.body.style.background = theme.colors.gray[1];
        return () => {
            document.body.style.background = originalBg;
        };
    }, []);
    const mspSvc = useDi(MspService);
    return (
        <Box p="xl">
            <Grid m={0}>
                <Grid.Col md={6} xl={4} offsetMd={3} offsetXl={4}>
                    <Card color={theme.white} shadow="xl" radius="lg" p={0}>
                        <Box p="xl">
                            <img src={mspSvc.defaultSupportAndLogos.Logo} style={{ width: 120 }} alt={mspSvc.defaultSupportAndLogos.CompanyName} />
                        </Box>
                        <Divider size="xs" />
                        <Accordion value={openSection} onChange={setSection}>
                            <Accordion.Item value="select" hidden={!hasCompanies} p={0}>
                                <Accordion.Control>
                                    <Title px="lg" order={4} sx={{ fontWeight: 'normal' }}>
                                        Select Registration
                                    </Title>
                                </Accordion.Control>
                                <Accordion.Panel p={0}>
                                    <SelectRegistration subscription={subscription} companies={companies} mspCompany={mspCompany} />
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Accordion>
                    </Card>
                </Grid.Col>
            </Grid>
        </Box>
    );
}

function SelectRegistration({
    companies,
    subscription,
    mspCompany,
}: {
    companies: Company[];
    subscription: AwsMarketplaceSubscription;
    mspCompany: Company | null;
}) {
    const formatSvc = useDi(FormatService);
    const [mspDbName, setMspDbName] = useState<string | undefined>();
    useEffect(() => {
        if (mspCompany != null) {
            setMspDbName(mspCompany.DatabaseName!.toLocaleLowerCase());
        }
    }, []);
    return (
        <Box p="md" sx={{ maxHeight: 400, overflow: 'auto' }}>
            {companies.map((c) => (
                <Fragment key={c.Id}>
                    <UnstyledButton
                        component="a"
                        href={
                            mspDbName === '' || mspDbName == undefined
                                ? `/${c.DatabaseName}/settings/payment-methods/aws-confirm-payment-method@id:${subscription.Id}`
                                : `/${mspDbName}/manage-company@id:${c.Id}/payment-methods/aws-confirm-payment-method@id:${subscription.Id}`
                        }
                    >
                        <SelectRegistrationContainer>
                            <Card px="xl" radius="lg" sx={{ background: 'none' }}>
                                <Group position="apart">
                                    <Box sx={{ flex: 1 }}>
                                        <Title order={5} sx={{ fontWeight: 'normal' }}>
                                            {c.CompanyName}
                                        </Title>
                                        <Text color="dimmed" size="sm">
                                            Date: {formatSvc.formatDate(formatSvc.toLocalDate(c.DateCreated))}
                                        </Text>
                                    </Box>
                                    <Text color="primary" size="sm" className="manage">
                                        Manage Billing
                                    </Text>
                                    <ChevronRight strokeWidth={1} />
                                </Group>
                            </Card>
                        </SelectRegistrationContainer>
                    </UnstyledButton>
                </Fragment>
            ))}
        </Box>
    );
}

const SelectRegistrationContainer = styled.div`
    > div {
        transition: all 0.25s linear;
        .manage {
            opacity: 0;
            transition: all 0.25s linear;
        }
        :hover {
            background: ${(p) => p.theme.colors.primary[1]};
            .manage {
                opacity: 1;
            }
        }
    }
`;
